import {
  ETIQUETA_ESTADO_DE_DOCUMENTO_DE_CUMPLIMIENTO,
  ETIQUETA_ESTADO_DE_DOCUMENTO_DE_CUMPLIMIENTO_EN,
  EXTENSIONES_DE_CUMPLIMIENTO,
  type DocumentoDeCumplimiento,
  type SituacionDeCumplimiento,
} from '@cnc/contracts';
import {useEffect, useState} from 'react';
import {
  enlaceDeArchivoDeCumplimiento,
  useCargarDocumentoDeCumplimiento,
  useAplicacionDeTipo,
  useEditarDocumentoDeCumplimiento,
  useHistorialDeCumplimiento,
  useRevisarDocumentoDeCumplimiento,
} from '../api/cumplimiento';
import {et, tr} from '../i18n/idioma';
import {fecha, fechaHora} from '../utilidades/formato';
import {
  ESTILO_DE_SITUACION,
  etiquetaDeSituacion,
  nombreDelTipo,
  tamanoLegible,
  textoDeDias,
} from './cumplimiento-estilos';
import {AYUDA_ARCHIVO, AYUDA_DOCUMENTO as AD, AYUDA_RESUMEN_CUMPLIMIENTO} from './ayuda-cumplimiento';
import {ElegirArchivo} from './elegir-archivo';
import {NotaDeAyuda} from './nota-de-ayuda';
import {usePermisos, useUsuario} from './sesion-contexto';

// Piezas comunes de Cumplimiento (decision 0045): la ficha de cada situacion y
// el panel de un tipo de documento en un vinculo (historial, carga, fechas y
// revision).

export function FichaDeSituacion({situacion, detalle}: {situacion: SituacionDeCumplimiento; detalle?: string}) {
  const estilo = ESTILO_DE_SITUACION[situacion];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs whitespace-nowrap ${estilo.clase}`}
    >
      <span aria-hidden>{estilo.icono}</span>
      {etiquetaDeSituacion(situacion)}
      {detalle && <span className="opacity-80">· {detalle}</span>}
    </span>
  );
}

// Enlace de descarga: cualquier rol (Daniel, 12/10/2026); queda en la bitacora.
export function DescargaDeDocumento({d}: {d: DocumentoDeCumplimiento}) {
  if (!d.archivo) return null;
  return (
    <a
      href={enlaceDeArchivoDeCumplimiento(d.id)}
      className="text-verde hover:underline"
      title={tr('Descargar', 'Download')}
    >
      ⇩ {d.archivo}
    </a>
  );
}

export function PanelDeDocumento({
  vinculoId,
  tipoId,
  onCerrar,
}: {
  vinculoId: number;
  tipoId: number;
  onCerrar: () => void;
}) {
  const historial = useHistorialDeCumplimiento(vinculoId, tipoId);
  const permisos = usePermisos();
  const h = historial.data;
  useEffect(() => {
    const cerrar = (e: KeyboardEvent) => e.key === 'Escape' && onCerrar();
    window.addEventListener('keydown', cerrar);
    return () => window.removeEventListener('keydown', cerrar);
  }, [onCerrar]);
  const enRevision = h?.documentos.find(d => d.estado === 'pending_review');
  const nombre = h ? nombreDelTipo(h.tipo, h.vinculo) : null;
  return (
    <div
      className="fixed inset-0 z-[1100] flex justify-end bg-black/40"
      onClick={e => e.target === e.currentTarget && onCerrar()}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={h ? `${nombre} · ${h.vinculo.nombre}` : tr('Documento', 'Document')}
        className="flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-linea bg-white p-5 shadow-2xl"
        data-prueba="panel-cumplimiento"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs tracking-wide text-apagado uppercase">{h?.vinculo.nombre ?? '…'}</p>
            <h2 className="text-lg font-semibold text-verde-oscuro">{nombre ?? tr('Cargando…', 'Loading…')}</h2>
            {h && nombre !== h.tipo.nombre && <p className="text-xs text-apagado">({h.tipo.nombre})</p>}
            {h?.tipo.descripcion && <p className="text-sm text-apagado">{h.tipo.descripcion}</p>}
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label={tr('Cerrar', 'Close')}
            className="rounded-md border border-linea px-2 py-1 text-sm hover:bg-papel"
          >
            ✕
          </button>
        </div>
        {historial.isError && <p className="mt-3 text-sm text-alerta">{historial.error.message}</p>}
        {h && (
          <div className="mt-4 space-y-5">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <FichaDeSituacion situacion={h.celda.situacion} />
              <NotaDeAyuda texto={AYUDA_RESUMEN_CUMPLIMIENTO.situacion} />
              {h.celda.venceEl && (
                <span className="text-apagado">
                  {tr('Vence el', 'Expires on')} {fecha(h.celda.venceEl)} ({textoDeDias(h.celda.diasParaVencer)})
                </span>
              )}
              <span className="text-xs text-apagado">
                {h.tipo.vigenciaMeses
                  ? tr(
                      `Vigencia: ${h.tipo.vigenciaMeses} meses desde la recepción · avisa ${h.tipo.diasDeAviso} días antes`,
                      `Validity: ${h.tipo.vigenciaMeses} months from receipt · warns ${h.tipo.diasDeAviso} days before`,
                    )
                  : tr(
                      `Sin vigencia fija: escribe la fecha de vencimiento · avisa ${h.tipo.diasDeAviso} días antes`,
                      `No fixed validity: write the expiry date · warns ${h.tipo.diasDeAviso} days before`,
                    )}
                {h.tipo.responsable &&
                  tr(` · Responsable: ${h.tipo.responsable}`, ` · Responsible: ${h.tipo.responsable}`)}
              </span>
            </div>
            {!h.vinculo.activo && (
              <p className="rounded-md border border-linea bg-papel px-3 py-2 text-xs text-apagado">
                {tr(
                  'Este vínculo está desactivado: no cuenta en el resumen ni avisa.',
                  'This link is deactivated: it does not count in the summary or warn.',
                )}
              </p>
            )}
            <Aplicacion
              vinculoId={vinculoId}
              version={h.vinculo.version}
              tipoId={tipoId}
              aplica={h.celda.aplica}
              editable={permisos.editar}
            />
            {enRevision && permisos.editar && <Revision documento={enRevision} />}
            {permisos.editar && (
              <CargaDeDocumento
                vinculoId={vinculoId}
                tipoId={tipoId}
                vigenciaMeses={h.tipo.vigenciaMeses}
                renovacion={h.documentos.length > 0}
              />
            )}
            <div>
              <h3 className="text-sm font-semibold tracking-wide text-apagado uppercase">
                {tr('Historial', 'History')} ({h.documentos.length})
              </h3>
              {!h.documentos.length ? (
                <p className="mt-1 text-sm text-apagado">
                  {tr('Todavía no hay ningún documento.', 'There is no document yet.')}
                </p>
              ) : (
                <ol className="mt-2 space-y-2">
                  {h.documentos.map(d => (
                    <DocumentoDelHistorial key={d.id} d={d} vigente={d.id === h.celda.documento?.id} />
                  ))}
                </ol>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

// Si este tipo de documento aplica a este vinculo. Si no aplica, no cuenta
// como faltante ni avisa; lo cargado se conserva.
function Aplicacion({
  vinculoId,
  version,
  tipoId,
  aplica,
  editable,
}: {
  vinculoId: number;
  version: number;
  tipoId: number;
  aplica: boolean;
  editable: boolean;
}) {
  const cambiar = useAplicacionDeTipo();
  return (
    <div
      className={`flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm ${aplica ? 'border-linea' : 'border-amber-300 bg-amber-50'}`}
      data-prueba="aplicacion-cumplimiento"
    >
      <span className="font-medium">
        {aplica
          ? tr('Este documento aplica a este vínculo.', 'This document applies to this link.')
          : tr('No aplica: no cuenta en el resumen ni avisa.', 'Does not apply: it does not count or warn.')}
      </span>
      {editable && (
        <button
          type="button"
          disabled={cambiar.isPending}
          onClick={() => cambiar.mutate({vinculoId, cambio: {version, tipoId, aplica: !aplica}})}
          className="rounded-md border border-linea bg-white px-2 py-1 text-xs hover:bg-papel disabled:opacity-50"
        >
          {aplica ? tr('Marcar "No aplica"', 'Mark "Does not apply"') : tr('Marcar "Sí aplica"', 'Mark "Applies"')}
        </button>
      )}
      {cambiar.isError && <span className="text-xs text-alerta">{cambiar.error.message}</span>}
    </div>
  );
}

function DocumentoDelHistorial({d, vigente}: {d: DocumentoDeCumplimiento; vigente: boolean}) {
  const permisos = usePermisos();
  const [corrigiendo, setCorrigiendo] = useState(false);
  const estado = et(ETIQUETA_ESTADO_DE_DOCUMENTO_DE_CUMPLIMIENTO, ETIQUETA_ESTADO_DE_DOCUMENTO_DE_CUMPLIMIENTO_EN)[
    d.estado
  ];
  const tono =
    d.estado === 'approved'
      ? 'text-verde-oscuro'
      : d.estado === 'rejected'
        ? 'text-alerta'
        : d.estado === 'pending_review'
          ? 'text-tinta'
          : 'text-apagado';
  return (
    <li className={`rounded-md border p-3 text-sm ${vigente ? 'border-verde/50' : 'border-linea'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className={`font-medium ${tono}`}>
          {estado}
          {vigente && <span className="ml-2 text-xs font-normal text-verde">({tr('vigente', 'current')})</span>}
        </span>
        <span className="text-xs text-apagado">
          {d.origen === 'carpeta'
            ? tr('De la carpeta de compliance', 'From the compliance folder')
            : `${tr('Subido el', 'Uploaded on')} ${fechaHora(d.cargadoEl)}${d.cargadoPor ? ` · ${d.cargadoPor}` : ''}`}
        </span>
      </div>
      <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 text-xs">
        <dt className="text-apagado">
          {tr('Recibido', 'Received')}
          <NotaDeAyuda titulo={tr('Recibido', 'Received')} texto={AYUDA_ARCHIVO.recibido} />
        </dt>
        <dd>{d.recibidoEl ? fecha(d.recibidoEl) : '—'}</dd>
        {d.expedidoEl && (
          <>
            <dt className="text-apagado">
              {tr('Expedido', 'Issued')}
              <NotaDeAyuda titulo={tr('Expedido', 'Issued')} texto={AD.expedido} />
            </dt>
            <dd>{fecha(d.expedidoEl)}</dd>
          </>
        )}
        {d.estado !== 'not_applicable' && (
          <>
            <dt className="text-apagado">
              {tr('Vence', 'Expires')}
              <NotaDeAyuda titulo={tr('Vence', 'Expires')} texto={AYUDA_ARCHIVO.vence} />
            </dt>
            <dd>{d.venceEl ? fecha(d.venceEl) : tr('No vence', 'Does not expire')}</dd>
          </>
        )}
        {d.referencia && (
          <>
            <dt className="text-apagado">
              {tr('Número', 'Number')}
              <NotaDeAyuda titulo={tr('Número', 'Number')} texto={AD.referencia} />
            </dt>
            <dd>{d.referencia}</dd>
          </>
        )}
        {d.rutaOriginal && (
          <>
            <dt className="text-apagado">
              {tr('Carpeta', 'Folder')}
              <NotaDeAyuda titulo={tr('Carpeta', 'Folder')} texto={AD.carpeta} />
            </dt>
            <dd className="break-all text-apagado">{d.rutaOriginal}</dd>
          </>
        )}
      </dl>
      {d.notas && <p className="mt-1 text-xs">{d.notas}</p>}
      {d.revisadoEl && d.origen === 'pantalla' && (
        <p className="mt-1 text-xs text-apagado">
          {tr('Revisado el', 'Reviewed on')} {fechaHora(d.revisadoEl)}
          {d.revisadoPor && ` · ${d.revisadoPor}`}
          {d.notaDeRevision && ` · “${d.notaDeRevision}”`}
        </p>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs">
        <DescargaDeDocumento d={d} />
        {d.tamanoBytes !== null && <span className="text-apagado">{tamanoLegible(d.tamanoBytes)}</span>}
        {permisos.editar && d.estado !== 'not_applicable' && !corrigiendo && (
          <button type="button" onClick={() => setCorrigiendo(true)} className="text-verde hover:underline">
            ✎ {tr('Corregir fechas', 'Fix dates')}
          </button>
        )}
      </div>
      {corrigiendo && <CorreccionDeFechas d={d} onListo={() => setCorrigiendo(false)} />}
    </li>
  );
}

// Corregir recepcion o vencimiento (p. ej. la fecha real de un pasaporte o una poliza).
function CorreccionDeFechas({d, onListo}: {d: DocumentoDeCumplimiento; onListo: () => void}) {
  const editar = useEditarDocumentoDeCumplimiento();
  const [recibidoEl, setRecibidoEl] = useState(d.recibidoEl ?? '');
  const [venceEl, setVenceEl] = useState(d.venceEl ?? '');
  const campo = 'mt-1 w-full rounded-md border border-linea bg-white px-2 py-1 text-sm';
  return (
    <form
      className="mt-2 grid gap-2 rounded-md bg-papel p-2 sm:grid-cols-[1fr_1fr_auto]"
      onSubmit={e => {
        e.preventDefault();
        editar.mutate(
          {
            id: d.id,
            edicion: {
              version: d.version,
              ...(recibidoEl !== (d.recibidoEl ?? '') ? {recibidoEl: recibidoEl || null} : {}),
              ...(venceEl !== (d.venceEl ?? '') ? {venceEl: venceEl || null} : {}),
            },
          },
          {onSuccess: onListo},
        );
      }}
    >
      <label className="text-xs text-apagado">
        {tr('Recibido', 'Received')}
        <NotaDeAyuda texto={AD.corregirRecibido} dentroDeEtiqueta />
        <input type="date" value={recibidoEl} onChange={e => setRecibidoEl(e.target.value)} className={campo} />
      </label>
      <label className="text-xs text-apagado">
        {tr('Vence (vacío = no vence)', 'Expires (empty = never)')}
        <NotaDeAyuda texto={AD.corregirVence} dentroDeEtiqueta />
        <input type="date" value={venceEl} onChange={e => setVenceEl(e.target.value)} className={campo} />
      </label>
      <div className="flex items-end gap-1">
        <button
          type="submit"
          disabled={editar.isPending}
          className="rounded-md bg-verde px-2 py-1 text-xs text-white hover:bg-verde-oscuro disabled:opacity-50"
        >
          {tr('Guardar', 'Save')}
        </button>
        <button type="button" onClick={onListo} className="rounded-md border border-linea bg-white px-2 py-1 text-xs">
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
      {editar.isError && <p className="text-xs text-alerta sm:col-span-3">{editar.error.message}</p>}
    </form>
  );
}

function Revision({documento}: {documento: DocumentoDeCumplimiento}) {
  const revisar = useRevisarDocumentoDeCumplimiento();
  const usuario = useUsuario();
  const [nota, setNota] = useState('');
  const propio = documento.cargadoPorId === usuario.id && usuario.rol !== 'admin';
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm" data-prueba="revision-cumplimiento">
      <p className="font-medium text-amber-900">
        {tr('Hay una versión nueva que espera revisión.', 'A new version is waiting for review.')}
      </p>
      {propio ? (
        <p className="mt-1 text-xs text-amber-900">
          {tr(
            'Tú la subiste: otra persona con permiso de edición debe aprobarla o rechazarla.',
            'You uploaded it: someone else with edit permission must approve or reject it.',
          )}
        </p>
      ) : (
        <>
          <label className="mt-2 block text-xs text-apagado">
            {tr('Nota (obligatoria para rechazar)', 'Note (required to reject)')}
            <NotaDeAyuda texto={AD.notaDeRevision} dentroDeEtiqueta />
            <input
              value={nota}
              onChange={e => setNota(e.target.value)}
              className="mt-1 w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta"
            />
          </label>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              disabled={revisar.isPending}
              onClick={() => revisar.mutate({id: documento.id, decision: 'aprobar', nota, version: documento.version})}
              className="rounded-md bg-verde px-3 py-1.5 text-sm text-white hover:bg-verde-oscuro disabled:opacity-50"
            >
              ✓ {tr('Aprobar', 'Approve')}
            </button>
            <button
              type="button"
              disabled={revisar.isPending || !nota.trim()}
              onClick={() => revisar.mutate({id: documento.id, decision: 'rechazar', nota, version: documento.version})}
              className="rounded-md border border-alerta px-3 py-1.5 text-sm text-alerta hover:bg-red-50 disabled:opacity-50"
            >
              ✕ {tr('Rechazar', 'Reject')}
            </button>
          </div>
        </>
      )}
      {revisar.isError && <p className="mt-2 text-xs text-alerta">{revisar.error.message}</p>}
    </div>
  );
}

function CargaDeDocumento({
  vinculoId,
  tipoId,
  vigenciaMeses,
  renovacion,
}: {
  vinculoId: number;
  tipoId: number;
  vigenciaMeses: number | null;
  renovacion: boolean;
}) {
  const cargar = useCargarDocumentoDeCumplimiento();
  const [abierto, setAbierto] = useState(!renovacion);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [recibidoEl, setRecibidoEl] = useState('');
  const [expedidoEl, setExpedidoEl] = useState('');
  const [venceEl, setVenceEl] = useState('');
  const [referencia, setReferencia] = useState('');
  const [notas, setNotas] = useState('');
  const limpiar = () => {
    setArchivo(null);
    setRecibidoEl('');
    setExpedidoEl('');
    setVenceEl('');
    setReferencia('');
    setNotas('');
  };
  if (!abierto)
    return (
      <button
        type="button"
        onClick={() => setAbierto(true)}
        className="rounded-md border border-verde bg-white px-3 py-1.5 text-sm text-verde-oscuro hover:bg-verde-claro"
      >
        ⇪ {tr('Subir una versión nueva', 'Upload a new version')}
      </button>
    );
  const listo = archivo !== null;
  const campo = 'mt-1 w-full rounded-md border border-linea bg-white px-2 py-1 text-sm';
  return (
    <form
      className="space-y-3 rounded-md border border-linea bg-papel p-3"
      onSubmit={e => {
        e.preventDefault();
        cargar.mutate(
          {vinculoId, tipoId, archivo, recibidoEl, expedidoEl, venceEl, referencia, notas, noAplica: false},
          {onSuccess: limpiar},
        );
      }}
      data-prueba="carga-cumplimiento"
    >
      <h3 className="text-sm font-semibold">
        {renovacion
          ? tr('Subir una versión nueva', 'Upload a new version')
          : tr('Subir el documento', 'Upload the document')}
      </h3>
      <>
        <ElegirArchivo
          etiqueta={tr('Archivo del documento', 'Document file')}
          accept={EXTENSIONES_DE_CUMPLIMIENTO.join(',')}
          archivos={archivo ? [archivo] : []}
          onElegir={a => setArchivo(a[0] ?? null)}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-apagado">
            {tr('Fecha de recepción (vacía = hoy)', 'Received on (empty = today)')}
            <NotaDeAyuda texto={AD.recibido} dentroDeEtiqueta />
            <input type="date" value={recibidoEl} onChange={e => setRecibidoEl(e.target.value)} className={campo} />
          </label>
          <label className="text-xs text-apagado">
            {tr('Fecha de expedición', 'Issue date')}
            <NotaDeAyuda texto={AD.expedido} dentroDeEtiqueta />
            <input type="date" value={expedidoEl} onChange={e => setExpedidoEl(e.target.value)} className={campo} />
          </label>
          <label className="text-xs text-apagado">
            {tr('Fecha de vencimiento', 'Expiry date')}
            <NotaDeAyuda texto={AD.vence} dentroDeEtiqueta />
            <input type="date" value={venceEl} onChange={e => setVenceEl(e.target.value)} className={campo} />
          </label>
          <label className="text-xs text-apagado">
            {tr('Número o referencia', 'Number or reference')}
            <NotaDeAyuda texto={AD.referencia} dentroDeEtiqueta />
            <input value={referencia} onChange={e => setReferencia(e.target.value)} className={campo} />
          </label>
        </div>
        <p className="text-xs text-apagado">
          {vigenciaMeses
            ? tr(
                `Sin fecha de vencimiento, se calcula: ${vigenciaMeses} meses desde la expedición o la recepción.`,
                `Without an expiry date, it is calculated: ${vigenciaMeses} months from issue or receipt.`,
              )
            : tr(
                'Este tipo no tiene vigencia fija: sin fecha de vencimiento, no vence.',
                'This type has no fixed validity: without an expiry date, it does not expire.',
              )}{' '}
          {tr(
            'Queda en revisión hasta que otra persona lo apruebe.',
            'It stays under review until someone else approves it.',
          )}
        </p>
      </>
      <label className="block text-xs text-apagado">
        {tr('Notas', 'Notes')}
        <NotaDeAyuda texto={AD.notas} dentroDeEtiqueta />
        <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2} className={campo} />
      </label>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!listo || cargar.isPending}
          className="rounded-md bg-verde px-3 py-1.5 text-sm text-white hover:bg-verde-oscuro disabled:opacity-50"
        >
          {cargar.isPending ? tr('Guardando…', 'Saving…') : tr('Guardar', 'Save')}
        </button>
        {renovacion && (
          <button
            type="button"
            onClick={() => {
              limpiar();
              setAbierto(false);
            }}
            className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
          >
            {tr('Cancelar', 'Cancel')}
          </button>
        )}
        {cargar.isSuccess && <span className="text-xs text-verde-oscuro">{tr('Guardado. ✓', 'Saved. ✓')}</span>}
      </div>
      {cargar.isError && <p className="text-xs text-alerta">{cargar.error.message}</p>}
    </form>
  );
}
